import React from 'react'
import { Link } from 'react-router-dom'
import { Card, CardBody } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Avatar } from '@/components/ui/Avatar'
import { AvailableMentor } from '@/types/matching'
import RatingSummary from './RatingSummary'

interface MentorCardProps {
  mentor: AvailableMentor
  onRequestMentor: (mentor: AvailableMentor) => void
  hasPendingRequest?: boolean
  hasActiveRelationship?: boolean
  matchScore?: number
}

const matchBadgeClasses = (matchScore: number): string => {
  if (matchScore >= 75) return 'bg-primary-100 text-primary-700'
  if (matchScore >= 50) return 'bg-gray-100 text-gray-700'
  return 'bg-gray-100 text-gray-500'
}

const AvailabilityDot: React.FC<{ available: boolean }> = ({ available }) => (
  <span
    className={`inline-block w-2 h-2 rounded-full ${available ? 'bg-accent-500' : 'bg-gray-300'}`}
  />
)

const MentorCard: React.FC<MentorCardProps> = ({
  mentor,
  onRequestMentor,
  hasPendingRequest = false,
  hasActiveRelationship = false,
  matchScore
}) => {
  const isAvailable = mentor.isAvailable && !mentor.isFull
  const canRequest = isAvailable && !hasPendingRequest && !hasActiveRelationship

  const getButtonText = () => {
    if (hasActiveRelationship) return 'Already Your Mentor'
    if (hasPendingRequest) return 'Request Pending'
    if (mentor.isFull) return 'Full'
    if (!isAvailable) return 'Not Available'
    return 'Request Mentor'
  }

  const getButtonVariant = (): 'primary' | 'secondary' | 'outline' => {
    if (hasActiveRelationship || hasPendingRequest) return 'secondary'
    if (!isAvailable) return 'outline'
    return 'primary'
  }

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardBody>
        <div className="flex items-start gap-5">
          {/* Avatar */}
          <Link to={`/users/${mentor.id}`} className="flex-shrink-0 relative">
            <Avatar
              src={mentor.profilePicture}
              name={`${mentor.firstName} ${mentor.lastName}`}
              size="lg"
            />
            <span
              className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-white ${
                isAvailable ? 'bg-accent-500' : 'bg-gray-300'
              }`}
            />
          </Link>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  <Link to={`/users/${mentor.id}`} className="hover:text-primary-700">
                    {mentor.firstName} {mentor.lastName}
                  </Link>
                </h3>
                <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                  <span className="flex items-center gap-1.5">
                    <AvailabilityDot available={isAvailable} />
                    {isAvailable ? 'Available' : 'Not available'}
                  </span>
                  <span className="text-gray-300">&middot;</span>
                  <span>
                    {mentor.currentMentees}/{mentor.maxMentees} mentees
                  </span>
                </div>
                <div className="mt-1.5">
                  <RatingSummary averageRating={mentor.averageRating} totalReviews={mentor.totalReviews} />
                </div>
              </div>

              {/* Status badge */}
              <div className="flex-shrink-0">
                {typeof matchScore === 'number' && (
                  <span
                    className={`px-3.5 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${matchBadgeClasses(matchScore)}`}
                  >
                    {matchScore}% match
                  </span>
                )}
                {hasActiveRelationship && (
                  <span className="px-3.5 py-1.5 bg-green-100 text-green-700 rounded-full text-xs font-bold whitespace-nowrap">
                    Your Mentor
                  </span>
                )}
                {hasPendingRequest && !hasActiveRelationship && (
                  <span className="px-3.5 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold whitespace-nowrap">
                    Pending
                  </span>
                )}
                {mentor.isFull && !hasActiveRelationship && !hasPendingRequest && (
                  <span className="px-3.5 py-1.5 bg-gray-100 text-gray-700 rounded-full text-xs font-bold whitespace-nowrap">
                    Full
                  </span>
                )}
              </div>
            </div>

            {/* Bio */}
            {mentor.bio && (
              <p className="mt-3 text-gray-600 text-sm leading-relaxed line-clamp-2">{mentor.bio}</p>
            )}

            {/* Expertise tags */}
            <div className="flex flex-wrap gap-2 mt-3.5">
              {mentor.expertise.slice(0, 5).map((skill) => (
                <span
                  key={skill}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold"
                >
                  {skill}
                </span>
              ))}
              {mentor.expertise.length > 5 && (
                <span className="px-3 py-1 bg-gray-100 text-gray-500 rounded-full text-xs font-semibold">
                  +{mentor.expertise.length - 5} more
                </span>
              )}
            </div>

            {/* Action button */}
            <div className="mt-5">
              <Button
                variant={getButtonVariant()}
                size="sm"
                onClick={() => onRequestMentor(mentor)}
                disabled={!canRequest}
              >
                {getButtonText()}
              </Button>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  )
}

export default MentorCard
